// The maternity hospital's own count of foetuses, against the one this pregnancy holds (KIN-2062).
//
// The backend applies a hospital count that is HIGHER than ours by itself: it creates the missing
// foetuses and raises the pregnancy to match (KIN-2059). A LOWER one it deliberately leaves alone,
// because a foetus may carry observations and removing one is a clinical decision — so it flags the
// message instead and leaves the judgement to the GP. Without this, the backend was asking a
// question nobody ever saw.
//
// `foetusCountDiffers` is recomputed on every read, so following the flag — rather than caching a
// decision here — is what makes the notice disappear once the two agree again.

/** "1 foetus" / "2 foetuses", so the sentence reads as English in the singular case. */
export const describeFoetusCount = (count) =>
  count === 1 ? "1 foetus" : `${count} foetuses`;

/**
 * The hospital's latest word on the count, when it disagrees with the pregnancy's, as
 * `{ reported, held }` — or null when the two agree.
 *
 * The latest message rather than any flagged one: the hospital restates the count on every message
 * it sends, and the backend flags each message against the current foetuses, so a disagreement a
 * newer message has already settled would otherwise keep the notice up for good.
 *
 * Returns null when a backend that predates KIN-2059 sends neither field, rather than rendering a
 * sentence with a blank in it.
 */
export const findHospitalFoetusCountMismatch = (inboundMessages, heldCount) => {
  if (!Number.isInteger(heldCount) || !Array.isArray(inboundMessages)) {
    return null;
  }

  const reporting = inboundMessages.filter((message) =>
    Number.isInteger(message?.reportedFoetusCount)
  );

  if (reporting.length === 0) {
    return null;
  }

  const latest = reporting.reduce((newest, message) =>
    (message.receivedAt ?? "") > (newest.receivedAt ?? "") ? message : newest
  );

  /*
   * The flag is read off the latest message only, not off any message carrying it: an earlier
   * disagreement the hospital has since restated is settled, and the backend leaves the old message
   * flagged because that message really did disagree at the time it was read.
   */
  if (latest.foetusCountDiffers !== true) {
    return null;
  }

  /*
   * The flag is computed against the live foetus rows while this panel shows the pregnancy's
   * declared count. They are kept in step, but if they ever drift we would rather say nothing than
   * print "reports 2 foetuses; this pregnancy holds 2".
   */
  if (latest.reportedFoetusCount === heldCount) {
    return null;
  }

  return { reported: latest.reportedFoetusCount, held: heldCount };
};
